/* eslint-disable @typescript-eslint/no-unused-vars */
'use client'
import React, { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useSearchParams } from 'next/navigation'

import { FORMAT_WISE_PLAYERS, SERIES_SQUAD_FORMAT } from '@/graphql/series/format.query'
import NoData from '../../noData'
import NoTeam from '../../../../assets/images/error-images/NoTeamsFound.svg'
import Loading from '../../loading'

const SquadsCard = dynamic(() => import('@/shared/components/series/seriesSquads/squadsCard'))
const SquadsTeamCard = dynamic(() => import('@/shared/components/series/seriesSquads/squadsTeamCard'))

type SquadsCardType = {
  oPlayer: {
    _id: string
    eFormat: string
    sSeason?: string
    sPlayingRole: string
    eTagStatus: string
    sFirstName: string
    sTitle?: string
    sFullName?: string
    oSeo: { sSlug: string; eType: string }
    oImg: { sUrl?: string } | null
  }
}

type SquadsTeamCardProps = {
  _id: string
  data?: { sFullName?: string; _id?: string; sTitle?: string; sFirstName?: string; oImg?: { sUrl?: string } }
  title?: string
  sTitle?: string
  activeTeam?: string
  nTotalPlayers?: number
}

type SeriesSquadsType = {
  squadsData?: {
    seriesTeamData?: { listSeriesTeams: { aTeams: SquadsTeamCardProps[] } }
    teamPlayer?: { listSeriesSquad: SquadsCardType[] }
  }
  seoData: any
  id?: string
}

function SeriesSquads({ id, squadsData, seoData }: SeriesSquadsType) {
  const searchParams = useSearchParams()

  const [tab, setTabs] = useState<any>()
  const [currentActiveTab, setCurrentActiveTab] = useState<any>()
  const [players, setPlayers] = useState<any>([])

  const newTeam = squadsData?.seriesTeamData?.listSeriesTeams?.aTeams?.filter(
    (data: SquadsTeamCardProps) => data?._id !== '622d96830961155e958ac6cf'
  )

  const teamNameRef = useRef<string | undefined>(newTeam?.[0]?.sTitle)
  const activeTeamRef: { current: SquadsTeamCardProps | undefined } = useRef(
    searchParams.get('teamid') ? newTeam?.find((data: SquadsTeamCardProps) => data?._id === searchParams.get('teamid')) : newTeam?.[0]
  )

  function getRoleWisePlayers(data: any) {
    const groupedPlayers: any = {
      wk: [],
      all: [],
      bowl: [],
      bat: []
    }
    data?.forEach((player: any) => {
      if (groupedPlayers[player.oPlayer?.sPlayingRole]) groupedPlayers[player.oPlayer?.sPlayingRole].push(player)
    })
    return groupedPlayers
  }

  const [getPlayers, { data, loading: playerLoading }] = useLazyQuery(FORMAT_WISE_PLAYERS, {
    onCompleted: (d) => {
      const roleWisePlayers = getRoleWisePlayers(d?.listSeriesSquadInFormat?.all)
      setPlayers(roleWisePlayers)
    }
  })

  const { loading } = useQuery(SERIES_SQUAD_FORMAT, {
    variables: { input: { _id: id } },
    onCompleted: (d: any) => {
      if (d?.getSeriesByIdFront?.sFormats?.length > 0 && newTeam && newTeam?.length > 0) {
        setTabs(d?.getSeriesByIdFront?.sFormats)
        setCurrentActiveTab(d?.getSeriesByIdFront?.sFormats[0])
        getPlayers({
          variables: { input: { eFormat: tab?.length > 1 ? currentActiveTab : null, iSeriesId: id, iTeamId: newTeam && newTeam[0]?._id } }
        })
      }
    }
  })

  useEffect(() => {
    if (searchParams.get('teamid')) {
      activeTeamRef.current = newTeam?.filter((data: SquadsTeamCardProps) => data?._id === searchParams.get('teamid'))?.[0]
    }
  }, [searchParams.get('teamid')])

  const handleTeam = (item: SquadsTeamCardProps) => {
    activeTeamRef.current = item
    teamNameRef.current = item?.sTitle
    getPlayers({
      variables: { input: { eFormat: tab?.length > 1 ? currentActiveTab.toLowerCase() : null, iSeriesId: id, iTeamId: item?._id } }
    })
  }

  const onClickTeamWrapper = (item: SquadsTeamCardProps) => () => handleTeam(item)

  function handleFormat(t: string) {
    setCurrentActiveTab(t)
    getPlayers({
      variables: {
        input: { eFormat: tab?.length > 1 ? t.replace(' ', '').toLowerCase() : null, iSeriesId: id, iTeamId: activeTeamRef.current?._id }
      }
    })
  }

  // Render players cards helper function
  const renderPlayersCards = () => {
    if (loading || playerLoading) {
      return <Loading className={'!bg-widget'} />
    } else {
      return (
        <>
          {players?.wk?.length > 0 && (
            <>
              <h4 className='text-primary-500 col-span-12 my-0 flex text-sm md:text-base text-nowrap'>{`Wicket keeper (${players?.wk?.length})`}</h4>
              {players?.wk?.map((p: SquadsCardType) => (
                <div key={p?.oPlayer?._id} className='col-span-6 md:col-span-4'>
                  <SquadsCard data={p?.oPlayer} />
                </div>
              ))}
              <div className='w-full h-[1px] bg-tabBorder col-span-12 mr-2'/>
            </>
          )}
          {players?.bat?.length > 0 && (
            <>
              <h4 className='text-primary-500 col-span-12 my-0  flex text-sm md:text-base text-nowrap'>{`Batsman (${players?.bat?.length})`}</h4>
              {players?.bat?.map((p: SquadsCardType) => (
                <div key={p?.oPlayer?._id} className='col-span-6 md:col-span-4'>
                  <SquadsCard data={p?.oPlayer} />
                </div>
              ))}
              <div className='w-full h-[1px] bg-tabBorder col-span-12 mr-2'/>
            </>
          )}
          {players?.all?.length > 0 && (
            <>
              <h4 className='text-primary-500 col-span-12 my-0 flex text-sm md:text-base text-nowrap'>{`All rounder (${players?.all?.length})`}</h4>
              {players?.all?.map((p: SquadsCardType) => (
                <div key={p?.oPlayer?._id} className='col-span-6 md:col-span-4'>
                  <SquadsCard data={p?.oPlayer} />
                </div>
              ))}
              <div className='w-full h-[1px] bg-tabBorder col-span-12 mr-2'/>
            </>
          )}
          {players?.bowl?.length > 0 && (
            <>
              <h4 className='text-primary-500 col-span-12 my-0 flex text-sm md:text-base text-nowrap '>{`Bowler (${players?.bowl?.length})`}</h4>
              {players?.bowl?.map((p: SquadsCardType) => (
                <div key={p?.oPlayer?._id} className='col-span-6 md:col-span-4'>
                  <SquadsCard data={p?.oPlayer} />
                </div>
              ))}
              <div className='w-full h-[1px] bg-tabBorder col-span-12 mr-2'/>
            </>
          )}
        </>
      )
    }
  }

  const currentTeam =  newTeam?.find((t : any) => t?._id === activeTeamRef?.current?._id)  

  return (
    <div className=''>
      <p className='text-sm md:text-lg font-bold text-textOffWhite mb-4 md:mb-6'>{`${seoData?.sTitle}`}  and Team</p>
      {newTeam && newTeam?.length > 0 ? (
        <div className={``}>
          <div className='md:hidden scroll-list h-fit'>
            {newTeam?.length !== 0 &&
              newTeam?.map((t: SquadsTeamCardProps) => {
                return (
                  <div key={t?._id} className='h-auto' onClick={onClickTeamWrapper(t)}>
                    <SquadsTeamCard data={t} playerLength={t?.nTotalPlayers} activeTeam={activeTeamRef?.current?._id} />
                  </div>
                )
              })}
          </div>
          <div className='grid grid-cols-12'>
            <div
              className={`${'bg-cardBg rounded-l-2xl border flex md:flex-col border-c w-56 p-0 md:w-full md:border-0 pr-6'} hidden md:block col-span-0 md:col-span-3`}
            >
              <div className='hidden md:block'>
                {newTeam?.length !== 0 &&
                  newTeam?.map((t: SquadsTeamCardProps) => {
                    return (
                      <div key={t?._id} className='col-span-6' onClick={onClickTeamWrapper(t)}>
                        <SquadsTeamCard data={t} playerLength={t?.nTotalPlayers} activeTeam={activeTeamRef?.current?._id} />
                      </div>
                    )
                  })}
              </div>
            </div>
            <div className={`md:bg-cardBg md:col-span-9 col-span-12 md:border-l border-l-c`}>
              <div className='flex md:hidden mx-4 mt-4 mb-2 text-center cursor-pointer items-center rounded-xl p-0 bg-c'>
                {tab?.length > 1 &&
                  tab?.map((t: string, i: any) => {
                    return (
                      <span
                        key={t}
                        onClick={() => handleFormat(t)}
                        className={`bg-c px-5 py-1 w-full uppercase ${
                          i === 0 ? 'rounded-l-xl' : i === tab?.length - 1 ? 'rounded-r-xl' : ''
                        } ${currentActiveTab === t && 'bg-primary-500 text-black'}`}
                      >
                        {t}
                      </span>
                    )
                  })}
              </div>
              <div className='grid grid-cols-12 gap-x-3 gap-y-7 md:px-6 dark:md:pr-0 pb-2'>
                <div className='col-span-12'>
              <SquadsTeamCard data={currentTeam} isHeading playerLength={data?.listSeriesSquadInFormat?.all?.length}/>
              </div>
                {renderPlayersCards()}</div>
            </div>
          </div>
        </div>
      ) : (
        <NoData
          imagePath={NoTeam}
          className='col-span-full'
          title='No squad was available yet! Looks like they’re still huddling for strategy.'
        />
      )}
    </div>
  )
}

export default SeriesSquads
